import { useRecoilState, useRecoilValue } from 'recoil';
import { mapValues, compact } from 'lodash';
import type { SubjectInputId, SubjectWithOptionalFee } from 'venn-state';
import { isPrivateSubject, multiSubjectInputGroupAnalysisSubjects, multiSubjectInputGroupSubjects } from 'venn-state';
import type { Portfolio } from 'venn-api';
import type { AnalysisSubject } from 'venn-utils';
import { assertNotNil, getBaseFee } from 'venn-utils';

const allIds = (portfolio: Portfolio): string[] =>
  compact([portfolio?.id?.toString() ?? portfolio?.fund?.id, ...(portfolio?.children ?? []).flatMap(allIds)]);

const getPublicSubjectId = (subject: SubjectWithOptionalFee): string | undefined => {
  switch (subject.type) {
    case 'portfolio':
      return subject.portfolioId.toString();
    case 'fund':
      return subject.fundId;
    default:
      return undefined;
  }
};

export const useFees = (
  subjectInputIds: SubjectInputId[],
): [
  Record<SubjectInputId, number[]>,
  (newFees: Record<SubjectInputId, number[]>, exclusions?: Record<SubjectInputId, string[][]>) => void,
] => {
  const [subjectGroups, setSubjectGroups] = useRecoilState(multiSubjectInputGroupSubjects(subjectInputIds));
  const studioSubjects = useRecoilValue(multiSubjectInputGroupAnalysisSubjects(subjectInputIds));
  const baselineFees = mapValues(subjectGroups, (subjects) => subjects.map(getBaseFee));
  const setBaselineFees = (
    newFees: Record<SubjectInputId, number[]>,
    exclusions?: Record<SubjectInputId, string[][]>,
  ) => {
    const newSubjectGroups: Record<SubjectInputId, SubjectWithOptionalFee[]> = {};
    for (const subjectGroup of Object.keys(subjectGroups)) {
      newSubjectGroups[subjectGroup as SubjectInputId] = subjectGroups[subjectGroup as SubjectInputId]!.map(
        (subject, index) => {
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          const studioSubject: AnalysisSubject = studioSubjects[subjectGroup][index];
          const ids = studioSubject?.portfolio ? allIds(studioSubject.portfolio) : [studioSubject?.fund?.id];
          return {
            ...subject,
            feesMapping: isPrivateSubject(subject)
              ? undefined
              : ids
                  // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
                  .filter((id) => exclusions?.[subjectGroup][index]?.includes(id))
                  .reduce((result, item) => ({ ...result, [assertNotNil(item)]: 0 }), {
                    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
                    [assertNotNil(getPublicSubjectId(subject))]: newFees[subjectGroup][index],
                  }),
          };
        },
      );
    }
    setSubjectGroups(newSubjectGroups);
  };
  return [baselineFees, setBaselineFees];
};
